import { start } from "repl";
import {
	AnnouncementExtensionSettingsUpdated, AnnouncementNewEventAddedToDatabase, AnnouncementRecordingCreated,
	AnnouncementRecordingDeleted, AnnouncementRecordingUpdated, isAnnouncementMessage,
	AnnouncementExtensionSettingsUpdatedMessageName, AnnouncementMessage, AnnouncementMessageName, AnnouncementNewEventAddedToDatabaseMessageName,
	AnnouncementRecordingCreatedMessageName, AnnouncementRecordingDeletedMessageName, AnnouncementRecordingUpdatedMessageName, 
	AnnouncementEventUpdatedInDatabase,
	AnnouncementEventUpdatedInDatabaseMessageName} from "../../constants";
import { SerializableError } from "../../lib/utils/SerializedException";
import { ProcessInfo } from "../ProcessInfo";
import { StateEvent } from "./StateEvent";



const sendMessage = async <T, R>(message: T) => {
	if (chrome != null && chrome.runtime != null) {
			try {
			return await chrome.runtime.sendMessage(message) as R;
		} catch (e) {
			const error = SerializableError.from(e, "sendMessage");
			if (!error.message.startsWith("Could not establish connection. Receiving end does not exist.")) {
				ProcessInfo.errorLog(`sendMessage error`, message, SerializableError.from(e, "sendMessage"));
			}
		}	
	}
}

const sendMessageAndIncludeContentScripts = async <T, R>(message: T) => {
	if (chrome != null && chrome.runtime != null) {
			try {
			const result = await sendMessage<T,R>(message);
			const tabIds = (await chrome.tabs.query({})).map(tab => tab.id).filter( (id): id is number => id != null);
			await Promise.all(tabIds.map( tabId =>
				chrome.tabs.sendMessage(tabId, message)
			));
			return result;
		} catch (e) {
			const error = SerializableError.from(e, "sendMessage");
			if (!error.message.startsWith("Could not establish connection. Receiving end does not exist.")) {
				ProcessInfo.errorLog(`sendMessageAndIncludeContentScripts error`, message, SerializableError.from(e, "sendMessage"));
			}
		}	
	}
}

/**
 * Whenever the writer changes a field, it should announce it to all the other
 * processes that may also be looking at the table, so that they can re-load their data.
 */
const announce = <T extends AnnouncementMessageName>(messageType: T, sendToContentScriptsAsWell: boolean = false) =>
	async (args: Omit<AnnouncementMessage & {messageType: T}, "messageType">) => {
	// ProcessInfo.log("Announcing", messageType, args);
	const message = {
		messageType,
		...args,
	} as AnnouncementMessage;
	// First act on the message locally
	// handleAnnouncementMessage(message);
	if (sendToContentScriptsAsWell) {
		return sendMessageAndIncludeContentScripts<AnnouncementMessage, void>(message);
	} else {
		return sendMessage<AnnouncementMessage, void>(message);
	}
};


const announceRecordingCreated = announce(AnnouncementRecordingCreatedMessageName);
const announceRecordingUpdated = announce(AnnouncementRecordingUpdatedMessageName);
const announceRecordingDeleted = announce(AnnouncementRecordingDeletedMessageName);
const announceNewEventAddedToDatabase = announce(AnnouncementNewEventAddedToDatabaseMessageName);
const announceEventUpdatedInDatabase = announce(AnnouncementEventUpdatedInDatabaseMessageName);
const announceExtensionSettingsUpdated = announce(AnnouncementExtensionSettingsUpdatedMessageName, true);

export const NewEventAddedToDatabase = new (class extends StateEvent<AnnouncementNewEventAddedToDatabase> {
	constructor() {super(AnnouncementNewEventAddedToDatabaseMessageName);}
	protected transmitToOtherProcesses(event: Omit<AnnouncementNewEventAddedToDatabase, "messageType">) {
		announceNewEventAddedToDatabase(event);
	}
})();

export const EventUpdatedInDatabase = new (class extends StateEvent<AnnouncementEventUpdatedInDatabase> {
	constructor() {super(AnnouncementEventUpdatedInDatabaseMessageName);}
	protected transmitToOtherProcesses(event: AnnouncementEventUpdatedInDatabase) {
		announceEventUpdatedInDatabase(event);
	}
})();

export const RecordingCreated = new (class extends StateEvent<AnnouncementRecordingCreated> {
	constructor() {super(AnnouncementRecordingCreatedMessageName);}
	protected transmitToOtherProcesses(event: AnnouncementRecordingCreated) {
		announceRecordingCreated(event);
	}
})();

export const RecordingUpdated = new (class extends StateEvent<AnnouncementRecordingUpdated> {
	constructor() {super(AnnouncementRecordingUpdatedMessageName);}
	protected transmitToOtherProcesses(event: AnnouncementRecordingUpdated) {
		announceRecordingUpdated(event);
	}
})();

export const RecordingDeleted = new (class extends StateEvent<AnnouncementRecordingDeleted> {
	constructor() {super(AnnouncementRecordingDeletedMessageName);}
	protected transmitToOtherProcesses(event: AnnouncementRecordingDeleted) {
		announceRecordingDeleted(event);
	}
})();

export const ExtensionSettingsUpdated = new (class extends StateEvent<AnnouncementExtensionSettingsUpdated> {
	constructor() {super(AnnouncementExtensionSettingsUpdatedMessageName);}
	protected transmitToOtherProcesses(event: AnnouncementExtensionSettingsUpdated) {
		announceExtensionSettingsUpdated(event);
	}
})();

const handleAnnouncementMessage = (message: AnnouncementMessage) => {
	switch(message.messageType) {
		case AnnouncementNewEventAddedToDatabaseMessageName:
			NewEventAddedToDatabase.emit(message);
			break;
		case AnnouncementRecordingCreatedMessageName:
			RecordingCreated.emit(message);
			break;
		case AnnouncementRecordingUpdatedMessageName:
			RecordingUpdated.emit(message);
			break;
		case AnnouncementRecordingDeletedMessageName:
			RecordingDeleted.emit(message);
			break;
		case AnnouncementExtensionSettingsUpdatedMessageName:
			ExtensionSettingsUpdated.emit(message);
			break;
	}
}

if ("chrome" in globalThis && chrome != null && chrome.runtime != null && chrome.runtime.onMessage != null && chrome.runtime.onMessage.addListener != null) {
	chrome.runtime.onMessage.addListener( async (message, sender) => {
		if (isAnnouncementMessage(message)) {
			handleAnnouncementMessage(message);
		}
	});
}